/** Ported from app/templates/components/visualization/score-timeline.html
 *  (Phase 4 of the port) — the DUAL timeline the two performance pages
 *  render: two mirrored WHOOP-gradient sparklines with a shared axis,
 *  moment dots with rich tooltips, hover scrubber and click-to-seek.
 *
 * Deliberately not ported: the single-timeline `render` (no caller once
 * the Jinja pages are gone), the legacy COLORS table it alone used, and
 * the debug console.log lines. Everything else is behavior-identical,
 * same imperative id-based API. CSS: legacy-css/score-timeline.css +
 * data-tooltip.css.
 */
import { buildTooltipHTML, formatTime, type MomentLike } from './momentUtils'

// Sparkline gradient - matches ScoreRing.PERFORMANCE_COLORS
const SPARKLINE_GRADIENT = {
  high: '#2BD9B0', // Teal - good/high performance
  mid: '#F4B942', // Warm Amber - medium
  low: '#E25555', // Muted Coral - bad/low
}

// Score thresholds for dot coloring (matching moment-field-map)
const SCORE_HIGH = 0.60
const SCORE_MEDIUM = 0.54

export type TimelinePoint = { frame: number; [key: string]: number | undefined }

export type TimelineSide = {
  team?: string
  data: TimelinePoint[]
  moments?: MomentLike[] | null
  onClick?: ((frame: number) => void) | null
  onMomentClick?: ((moment: MomentLike) => void) | null
}

export type DualOptions = {
  totalFrames?: number
  color?: string
  scoreField?: string
  minScore?: number
  maxScore?: number
  /** The game's frame rate (the server's fps): every time on the chart;
   *  null when the server has none, and the times read '-'. */
  fps: number | null
  neutralDotColor?: string | null
}

function getScoreLevel(score: number): string {
  if (score >= SCORE_HIGH) return 'good'
  if (score >= SCORE_MEDIUM) return 'okay'
  return 'bad'
}

/** Smooth stroke-only SVG path through the points (quadratic bezier). */
export function generatePath(data: TimelinePoint[], height: number, width: number,
                             scoreField: string,
                             options: { minScore?: number; maxScore?: number;
                                        totalFrames?: number } = {}): string {
  if (!data || data.length === 0) return ''

  const minScore = options.minScore || 0
  const maxScore = options.maxScore || 1
  const totalFrames = options.totalFrames || 54000
  const range = maxScore - minScore

  const points = data.map((point) => {
    const x = (point.frame / totalFrames) * width
    const score = point[scoreField] !== undefined ? point[scoreField]! : 0
    const normalized = Math.max(0, Math.min(1, (score - minScore) / range))
    const y = height - (normalized * height)
    return { x, y }
  })

  if (points.length === 0) return ''

  let path = `M ${points[0]!.x},${points[0]!.y}`
  for (let i = 0; i < points.length - 1; i++) {
    const curr = points[i]!
    const next = points[i + 1]!
    const cx = (curr.x + next.x) / 2
    path += ` Q ${cx},${curr.y} ${next.x},${next.y}`
  }
  return path
}

/** Timeline markers (15', 30', HT, 60', 75', 90'). */
function renderMarkers(container: HTMLElement): void {
  const markers = ["15'", "30'", 'HT', "60'", "75'", "90'"]
  const positions = [15, 30, 45, 60, 75, 90]
  container.innerHTML = markers.map((label, i) => {
    const pct = (positions[i]! / 90) * 100
    return `<span class="score-timeline__marker" style="left:${pct}%">${label}</span>`
  }).join('')
}

function renderMomentDots(dotsContainer: HTMLElement, moments: MomentLike[],
                          totalFrames: number,
                          onDotClick: ((m: MomentLike) => void) | null | undefined,
                          tooltipEl: HTMLElement | null,
                          neutralColor: string | null, fps: number | null): void {
  if (!dotsContainer || !moments || moments.length === 0) return

  dotsContainer.innerHTML = ''

  moments.forEach((moment) => {
    const dot = document.createElement('div')
    const score = moment.score || 0
    const level = neutralColor || getScoreLevel(score)

    dot.className = `score-timeline__dot score-timeline__dot--${level}`
    dot.style.left = `${((moment.frame ?? 0) / totalFrames) * 100}%`
    dot.dataset.frame = String(moment.frame ?? 0)
    dot.dataset.score = String(score)

    if (tooltipEl) {
      // The bottom (opponent) row is CSS-flipped; the tooltip positions
      // below in local coords (above visually) and counter-rotates.
      const isFlipped = dotsContainer.closest('.dual-score-timeline__row--bottom') !== null

      dot.addEventListener('mouseenter', () => {
        tooltipEl.innerHTML = buildTooltipHTML(moment, fps)
        const dotLeft = parseFloat(dot.style.left)
        tooltipEl.style.left = `${dotLeft}%`

        if (isFlipped) {
          tooltipEl.style.top = '100%'
          tooltipEl.style.bottom = 'auto'
          tooltipEl.style.transform = 'translateX(-50%) scaleY(-1)'
          tooltipEl.style.marginTop = '8px'
          tooltipEl.style.marginBottom = '0'
        } else {
          tooltipEl.style.bottom = '100%'
          tooltipEl.style.top = 'auto'
          tooltipEl.style.transform = 'translateX(-50%)'
          tooltipEl.style.marginBottom = '8px'
          tooltipEl.style.marginTop = '0'
        }
        tooltipEl.classList.add('visible')
      })
      dot.addEventListener('mouseleave', () => {
        tooltipEl.classList.remove('visible')
      })
    } else {
      dot.title = `${Math.round(score * 100)} • ${formatTime(moment.frame ?? 0, fps)}`
    }

    if (onDotClick) {
      dot.addEventListener('click', (e) => {
        e.stopPropagation()
        onDotClick(moment)
      })
    }

    dotsContainer.appendChild(dot)
  })
}

/** Internal: render one chart of the dual (WHOOP vertical gradient). */
function renderChart(id: string, data: TimelinePoint[], options: DualOptions & {
  moments?: MomentLike[] | null
  onClick?: ((frame: number) => void) | null
  onMomentClick?: ((moment: MomentLike) => void) | null
}): void {
  const svg = document.getElementById(`${id}-svg`)
  const chart = document.getElementById(`${id}-chart`)
  const scrubber = document.getElementById(`${id}-scrubber`)
  const tooltip = document.getElementById(`${id}-tooltip`)
  const dotsContainer = document.getElementById(`${id}-dots`)
  const momentTooltip = document.getElementById(`${id}-moment-tooltip`)

  if (!svg || !chart) return

  const {
    scoreField = 'score',
    totalFrames = 54000,
    minScore = 0,
    maxScore = 1,
    fps,
    onClick = null,
    moments = null,
    onMomentClick = null,
  } = options

  const width = 1200, height = 28
  const strokePadding = 2 // Extra viewBox space so the stroke is visible at min/max

  const path = generatePath(data, height, width, scoreField, { minScore, maxScore, totalFrames })

  // Vertical gradient: high scores (top) teal, low (bottom) coral. The
  // bottom chart is CSS scaleY(-1) flipped, which flips path AND gradient
  // together, so the same gradient serves both charts.
  const gradientStops = `
      <stop offset="0%" stop-color="${SPARKLINE_GRADIENT.high}" stop-opacity="0.85" />
      <stop offset="50%" stop-color="${SPARKLINE_GRADIENT.mid}" stop-opacity="0.85" />
      <stop offset="100%" stop-color="${SPARKLINE_GRADIENT.low}" stop-opacity="0.85" />`

  svg.setAttribute('viewBox', `0 -${strokePadding} ${width} ${height + strokePadding * 2}`)
  svg.setAttribute('preserveAspectRatio', 'none')
  svg.innerHTML = `
      <defs>
        <linearGradient id="${id}-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          ${gradientStops}
        </linearGradient>
      </defs>
      <path d="${path}" fill="none" stroke="url(#${id}-gradient)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    `

  if (scrubber) scrubber.style.background = SPARKLINE_GRADIENT.mid
  if (tooltip) tooltip.style.background = SPARKLINE_GRADIENT.mid

  if (moments && dotsContainer) {
    // Upstream fell back to onClick here — a frame handler in a moment
    // handler's seat, which no live call path ever exercised (the top row
    // always passes onMomentClick; the bottom row passes neither). The
    // typed port drops the ill-typed fallback rather than casting it.
    renderMomentDots(dotsContainer, moments, totalFrames,
                     onMomentClick ?? null, momentTooltip, options.neutralDotColor ?? null,
                     fps)
  }

  if (onClick) {
    chart.onmousemove = (e) => {
      const rect = chart.getBoundingClientRect()
      const x = e.clientX - rect.left
      const pct = Math.max(0, Math.min(1, x / rect.width))
      const frame = Math.round(pct * totalFrames)
      if (scrubber) scrubber.style.left = `${pct * 100}%`
      if (tooltip) {
        tooltip.style.left = `${pct * 100}%`
        tooltip.textContent = formatTime(frame, fps)
      }
    }
    chart.onclick = (e) => {
      const rect = chart.getBoundingClientRect()
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      onClick(Math.round(pct * totalFrames))
    }
  }
}

/** Render the dual timeline (viewing team on top, opponent mirrored below). */
export function renderDual(id: string, topData: TimelineSide,
                           bottomData: TimelineSide, options: DualOptions): void {
  const { totalFrames = 54000, scoreField = 'tactical_score',
          neutralDotColor = null } = options
  const markers = document.getElementById(`${id}-markers`)

  const topTeamEl = document.getElementById(`${id}-top-team`)
  const bottomTeamEl = document.getElementById(`${id}-bottom-team`)
  if (topTeamEl) topTeamEl.textContent = topData.team ?? ''
  if (bottomTeamEl) bottomTeamEl.textContent = bottomData.team ?? ''

  if (markers) renderMarkers(markers)

  renderChart(`${id}-top`, topData.data, {
    ...options,
    totalFrames,
    scoreField,
    moments: topData.moments,
    onClick: topData.onClick,
    onMomentClick: topData.onMomentClick,
    neutralDotColor,
  })

  renderChart(`${id}-bottom`, bottomData.data, {
    ...options,
    totalFrames,
    scoreField,
    moments: bottomData.moments ?? null,
    onClick: null,
    neutralDotColor,
  })
}
